/**
 * Parses various date representations into a Date instance.
 * @param {*} value - Date, number (timestamp), string (YYYY-MM-DD or ISO), or object with toDate().
 * @returns {Date|null}
 */
export function parseDateValue(value) {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'object' && typeof value.toDate === 'function') return value.toDate();
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
        const [datePart, timePart] = value.split(' ');
        const datePieces = datePart.split('-').map((piece) => Number(piece));
        if (datePieces.length === 3 && datePieces.every((piece) => Number.isFinite(piece))) {
            const [year, month, day] = datePieces;
            let hours = 0;
            let minutes = 0;
            let seconds = 0;
            if (timePart) {
                const timePieces = timePart.split(':').map((piece) => Number(piece));
                hours = timePieces[0] || 0;
                minutes = timePieces[1] || 0;
                seconds = timePieces[2] || 0;
            }
            return new Date(year, month - 1, day, hours, minutes, seconds);
        }
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Formats a date value for chart labels by rangeType.
 * @param {*} value - Value passed to parseDateValue.
 * @param {string} rangeType - 'hours' | 'dayMonth' | 'month' | 'week'
 * @param {{ useFullMonth?: boolean }} [opts]
 * @returns {string}
 */
export function formatChartLabel(value, rangeType, { useFullMonth } = {}) {
    const d = parseDateValue(value);
    if (!d) return String(value);
    if (rangeType === 'hours') {
        return d.toLocaleString('en-US', { hour: 'numeric', hour12: true });
    }
    if (rangeType === 'week') {
        const weekStart = d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const weekEnd = new Date(d);
        weekEnd.setDate(weekEnd.getDate() + 6);
        const weekEndStr = weekEnd.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${weekStart} – ${weekEndStr}`;
    }
    if (rangeType === 'dayMonth') {
        return d.toLocaleString('en-US', {
            month: useFullMonth ? 'long' : 'short',
            day: 'numeric',
            year: 'numeric',
        });
    }
    return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
}
